import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ImageModule } from '@df/module-image/image.module';
import {
    ButtonsOptions,
    ColorOptions,
    FormSizeOptions,
    HorizontalAlignmentOptions,
    HtmlTagOptions,
    LayoutWidthOptions,
    RatioOptions,
    TextStyleOptions,
    UndefinedOption
} from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { VimeoPlayerComponent } from '@df/ui/display/media/vimeo-player.component';
import { YoutubePlayerComponent } from '@df/ui/display/media/youtube-player.component';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ShellComponent } from '../../ui/display/local-shell.component';
import { TagComponent } from '../../ui/display/tag/tag.component';
export namespace NCmsHeroImage {
    export interface IImageLineData {
        type: 'button' | 'text';
        align: 'left' | 'center' | 'right';
        form_size: string;
        button_class: 'button-1' | 'button-2' | 'button-inline';
        button_link: string;
        text: string;
        text_style: string;
        color: string;
        style: string;
        tag: string;
    }
    export interface IImageBlockData {
        style: string;
        custom: boolean;
        components: NICmsContentComponent.IData[];
        top_position: number;
        left_position: number;
        right_position: number;
        lines: IImageLineData[];
    }
    export interface IComponentData extends NICmsContentComponent.IData {
        link: string;
        video_hide: boolean;
        ratio: string;
        background_image: string;
        video_id: string;
        video_provider: 'youtube' | 'vimeo' | string;
        video_autoplay: boolean;
        video_background: boolean;
        width: string;
        blocks: NCmsHeroImage.IImageBlockData[];
    }
}
@Component({
    selector: 'cms-hero-image',
    templateUrl: 'cms-hero-image.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        RouterLink,
        ICmsClientModule,
        TagComponent,
        VimeoPlayerComponent,
        YoutubePlayerComponent,
        ImageModule,
        ShellComponent,
        HrefComponent
    ]
})
@ICmsComponent<CmsHeroImageComponent>({
    id: 'hero-image',
    name: 'Hero Image',
    // image: '/common/cms/hero-image-component.png',
    description: 'Wide banner with text lines and optional link',
    tags: ['banner', 'link', 'big'],
    fields: [
        // general
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-general',
            title: 'General'
        },
        {
            key: 'link',
            type: NICmsContentField.EFieldType.text,
            title: 'Link',
            description:
                'Please use path like /some-path for internal usage or absolute url https://example.com/some-page for external reference'
        },
        // background
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-background',
            title: 'Background'
        },
        <NICmsContentField.IChoiceField>{
            key: 'ratio',
            type: NICmsContentField.EFieldType.choice,
            title: 'Ratio',
            options: RatioOptions,
            description: 'Proportions width x height',
            default: 'ratio-2-1'
        },
        {
            key: 'background_image',
            type: NICmsContentField.EFieldType.image,
            title: 'Image'
        },
        // video
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-positioning',
            title: 'Video'
        },
        {
            key: 'video_id',
            type: NICmsContentField.EFieldType.text,
            title: 'ID or URL',
            description: 'Video ID or URL'
        },
        <NICmsContentField.IChoiceField>{
            key: 'video_provider',
            type: NICmsContentField.EFieldType.choice,
            title: 'Provider',
            options: [
                ...UndefinedOption,
                {
                    key: 'youtube',
                    title: 'YouTube'
                },
                {
                    key: 'vimeo',
                    title: 'Vimeo'
                }
            ],
            description: 'Choose a video provider'
        },
        {
            key: 'video_autoplay',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Autoplay',
            default: true
        },
        {
            key: 'video_background',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Background Mode',
            default: true
        },
        {
            key: 'video_hide',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Hide',
            default: false
        },
        // content
        {
            type: NICmsContentField.EFieldType.label,
            key: 'section-content',
            title: 'Content'
        },
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            title: 'Content Width',
            key: 'width',
            itemName: 'width',
            options: LayoutWidthOptions,
            default: undefined
        },
        // Text blocks
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'blocks',
            title: 'Content Blocks',
            itemName: 'Block',
            itemNameField: 'block',
            defaultSize: 1,
            fields: [
                // general
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-general',
                    title: 'General'
                },
                {
                    key: 'custom',
                    type: NICmsContentField.EFieldType.toggle,
                    title: 'Use Custom Components',
                    default: false
                },
                <NICmsContentField.IComponentsField>{
                    key: 'components',
                    type: NICmsContentField.EFieldType.components,
                    title: 'Components',
                    itemName: 'component',
                    default: [],
                    canShow: (data: NCmsHeroImage.IImageBlockData) => data.custom
                },
                {
                    key: 'style',
                    type: NICmsContentField.EFieldType.text,
                    title: 'Custom Styling',
                    description: 'Direct CSS. Please be careful when changing this.',
                    canShow: (data: NCmsHeroImage.IImageBlockData) => !data.custom
                },
                // text posistioning
                {
                    type: NICmsContentField.EFieldType.label,
                    key: 'blocks-positioning',
                    title: 'Positioning'
                },
                <NICmsContentField.INumberField>{
                    key: 'left_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Left',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 0,
                    canShow: (data: NCmsHeroImage.IImageBlockData) => !data.custom
                },
                <NICmsContentField.INumberField>{
                    key: 'top_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Top',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 50
                },
                <NICmsContentField.INumberField>{
                    key: 'right_position',
                    type: NICmsContentField.EFieldType.number,
                    title: 'Shift From Right',
                    min: 0,
                    max: 100,
                    step: 1,
                    default: 0
                },
                // text lines
                <NICmsContentField.IRepeaterField>{
                    type: NICmsContentField.EFieldType.repeater,
                    key: 'lines',
                    title: 'Text Lines',
                    itemName: 'Line',
                    itemNameField: 'text',
                    canShow: (data: NCmsHeroImage.IImageBlockData) => !data.custom,
                    defaultSize: 1,
                    fields: [
                        <NICmsContentField.IChoiceField>{
                            key: 'type',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Line Type',
                            options: [
                                { key: 'text', title: 'Text Line' },
                                { key: 'button', title: 'Button' }
                            ],
                            description: 'Line type',
                            default: 'text'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'align',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Alignment',
                            options: HorizontalAlignmentOptions,
                            default: 'left'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'button_class',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Button Style',
                            canShow: (data: NCmsHeroImage.IImageLineData) => data.type === 'button',
                            options: ButtonsOptions,
                            default: ButtonsOptions[0] ? ButtonsOptions[0].key : undefined
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'form_size',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Button Size',
                            canShow: (data: NCmsHeroImage.IImageLineData) => data.type === 'button',
                            options: FormSizeOptions
                        },
                        {
                            key: 'button_link',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Button Link',
                            description: 'If component link is set it will be used unless overriden here.',
                            canShow: (data: NCmsHeroImage.IImageLineData) => data.type === 'button'
                        },
                        {
                            key: 'text',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Copy',
                            default: 'This is new line'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'text_style',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Style',
                            options: TextStyleOptions,
                            canShow: (data: NCmsHeroImage.IImageLineData) => data.type !== 'button'
                        },
                        {
                            key: 'style',
                            type: NICmsContentField.EFieldType.text,
                            title: 'Custom Styling',
                            description: 'Direct CSS. Please be careful when changing this.',
                            protected: true
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'color',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Text Color',
                            options: ColorOptions,
                            colorPreview: true,
                            canShow: (data: NCmsHeroImage.IImageLineData) => data.type !== 'button' || data.button_class === 'button-inline'
                        },
                        <NICmsContentField.IChoiceField>{
                            key: 'tag',
                            type: NICmsContentField.EFieldType.choice,
                            title: 'Force Tag',
                            description: 'For SEO reasons you may want to force particular HTML tag',
                            options: [...UndefinedOption, ...HtmlTagOptions],
                            canShow: (data: NCmsHeroImage.IImageLineData) => data.type !== 'button'
                        }
                    ]
                }
            ]
        }
    ]
})
export class CmsHeroImageComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'component'
    })
    component?: NCmsHeroImage.IComponentData;
}
